import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useMutation } from "@tanstack/react-query";
import { useActiveSurvey } from "@/hooks/useActiveSurvey.js";
import { useLangStore } from "@/store/lang.js";
import { PublicNav } from "@/components/PublicNav.js";
import { ProgressNav } from "@/components/ProgressNav.js";
import { ConfirmModal } from "@/components/ConfirmModal.js";
import { Skeleton } from "@bongeunsa/ui";
import { uiStrings } from "@bongeunsa/i18n";
import { resolveText, resolveTitle } from "@/lib/i18nResolve.js";
import { usePageTitle } from "@/hooks/usePageTitle.js";
import { getNationalityOptions } from "@/lib/nationalities.js";
import { api } from "@/lib/api.js";
import type { Language } from "@bongeunsa/i18n";
import type { SurveyChoice } from "@bongeunsa/db";

type PersonalInfo = { name?: string; age?: number; gender?: string; nationality?: string };
type Step = { type: "consent" } | { type: "question"; index: number };

function getSavedKey(surveyId: string) { return `survey_response_${surveyId}`; }

export function SurveyPage() {
  const { slug } = useParams<{ slug: string }>();
  const { lang } = useLangStore();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useActiveSurvey(slug);
  const s = uiStrings[lang];

  const surveyId = data?.survey?.id;

  const [responseId, setResponseId] = useState<string | null>(() => {
    if (!surveyId) return null;
    return localStorage.getItem(getSavedKey(surveyId));
  });
  const [step, setStep] = useState<Step>(() =>
    responseId ? { type: "question", index: 0 } : { type: "consent" }
  );
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [personalInfo, setPersonalInfo] = useState<PersonalInfo>({});
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [direction, setDirection] = useState(1);

  const questionIndex = step.type === "question" ? step.index : null;
  useEffect(() => {
    if (questionIndex === null) return;
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [questionIndex]);

  const startMutation = useMutation({
    mutationFn: async (info: PersonalInfo) => {
      const res = await api.post<{ response_id: string }>(`/api/surveys/${surveyId}/responses`, { language: lang, personal_info: info });
      return res.response_id;
    },
    onSuccess: (rid) => {
      setResponseId(rid);
      if (surveyId) localStorage.setItem(getSavedKey(surveyId), rid);
      setStep({ type: "question", index: 0 });
    },
  });

  const answerMutation = useMutation({
    mutationFn: async ({ questionId, choiceId }: { questionId: string; choiceId: string }) => {
      if (!responseId || !surveyId) return;
      await api.post(`/api/surveys/${surveyId}/responses/${responseId}/answers`, { answers: [{ question_id: questionId, choice_id: choiceId }] });
    },
  });

  const completeMutation = useMutation({
    mutationFn: async () => {
      if (!responseId || !surveyId) return;
      await api.post(`/api/surveys/${surveyId}/responses/${responseId}/complete`);
    },
    onSuccess: () => {
      if (surveyId) localStorage.removeItem(getSavedKey(surveyId));
      navigate(`/s/${slug}/done`);
    },
  });

  usePageTitle(s.site.surveyTitle);

  if (isLoading) return <SurveySkeleton />;
  if (isError || !data?.survey || !data.survey.is_active) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-8">
        <div className="glass-card w-full max-w-xs rounded-2xl px-8 py-8 text-center">
          <p className="text-gray-500">{s.survey.errorLoad}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 text-sm text-brand-500 underline"
          >
            {s.common.retry}
          </button>
        </div>
      </div>
    );
  }

  const { survey, questions } = data;
  const total = questions.length;
  const defaultLang = (survey.default_language as Language) || "ko";
  const nationalityOptions = getNationalityOptions(lang);
  const surveyTitle = resolveTitle(survey.name_i18n, lang, defaultLang, survey.name);

  function getI18n(i18nStr: string) {
    const obj = JSON.parse(i18nStr) as Record<Language, Record<string, string>>;
    return resolveText(obj, lang, defaultLang);
  }

  function handleHomeClick() {
    if (step.type === "question") setShowExitConfirm(true);
    else navigate("/");
  }

  function confirmExit() {
    if (surveyId) localStorage.removeItem(getSavedKey(surveyId));
    navigate("/");
  }

  function handleBack() {
    if (step.type !== "question" || step.index <= 0) return;
    setDirection(-1);
    setStep({ type: "question", index: step.index - 1 });
  }

  // ── Consent ──────────────────────────────────────────────────

  if (step.type === "consent") {
    return (
      <div className="flex min-h-screen flex-col pt-3">
        <PublicNav lang={lang} onHomeClick={() => navigate("/")} title={surveyTitle} />
        <div className="px-3 pt-3 pb-8">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="flex flex-col gap-5 rounded-2xl glass-card px-5 py-6"
          >
            <h1 className="text-2xl font-bold text-gray-900">{surveyTitle}</h1>

            {survey.collect_personal_info ? (
              <div className="space-y-3">
                <FInput
                  label={`${s.survey.personalInfo.name} (${lang === "ko" ? "선택" : "optional"})`}
                  value={personalInfo.name ?? ""}
                  onChange={(v) => setPersonalInfo((p) => ({ ...p, name: v }))}
                />
                <FInput
                  label={`${s.survey.personalInfo.age} (${lang === "ko" ? "선택" : "optional"})`}
                  type="number"
                  value={personalInfo.age?.toString() ?? ""}
                  onChange={(v) => setPersonalInfo((p) => ({ ...p, age: v ? parseInt(v) : undefined }))}
                />
                <FSelect
                  label={`${s.survey.personalInfo.gender} (${lang === "ko" ? "선택" : "optional"})`}
                  value={personalInfo.gender ?? ""}
                  onChange={(v) => setPersonalInfo((p) => ({ ...p, gender: v }))}
                  options={[
                    { value: "", label: s.personalInfo.notSay },
                    { value: "male", label: s.personalInfo.male },
                    { value: "female", label: s.personalInfo.female },
                    { value: "other", label: s.personalInfo.other },
                  ]}
                />
                <FSelect
                  label={`${s.survey.personalInfo.nationality} (${lang === "ko" ? "선택" : "optional"})`}
                  value={personalInfo.nationality ?? ""}
                  onChange={(v) => setPersonalInfo((p) => ({ ...p, nationality: v }))}
                  options={nationalityOptions}
                />
              </div>
            ) : null}

            <div className="space-y-2.5">
              <FCheckbox checked={agreedTerms} onChange={setAgreedTerms} label={s.survey.personalInfo.agreeTerms} />
              <FCheckbox checked={agreedPrivacy} onChange={setAgreedPrivacy} label={s.survey.personalInfo.agreePrivacy} />
            </div>

            <button
              disabled={!agreedTerms || !agreedPrivacy || startMutation.isPending}
              onClick={() => startMutation.mutate(personalInfo)}
              className="h-12 w-full rounded-xl bg-brand-500 text-base font-semibold text-white transition-colors hover:bg-brand-600 active:bg-brand-700 disabled:opacity-40"
            >
              {startMutation.isPending ? "..." : s.survey.start}
            </button>
            {startMutation.isError && (
              <p className="text-center text-sm text-red-500">{s.common.error}. {s.common.retry}.</p>
            )}
          </motion.div>
        </div>
      </div>
    );
  }

  // ── Question ──────────────────────────────────────────────────

  if (step.type === "question") {
    const qIdx = step.index;
    const question = questions[qIdx];
    if (!question) return null;
    const qi18n = getI18n(question.i18n);
    const hasAnswered = !!answers[question.id];

    async function handleChoice(choice: SurveyChoice) {
      setAnswers((prev) => ({ ...prev, [question!.id]: choice.id }));
      await answerMutation.mutateAsync({ questionId: question!.id, choiceId: choice.id });
      if (qIdx < total - 1) {
        setDirection(1);
        setStep({ type: "question", index: qIdx + 1 });
      } else {
        completeMutation.mutate();
      }
    }

    return (
      <div className="flex min-h-screen flex-col pt-3">
        <PublicNav lang={lang} onHomeClick={handleHomeClick} title={surveyTitle} />
        <ProgressNav
          current={qIdx + 1}
          total={total}
          onBack={handleBack}
          canGoBack={qIdx > 0}
        />

        <div className="px-3 pt-3 pb-8">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={qIdx}
              custom={direction}
              variants={{
                enter: (dir: number) => ({ opacity: 0, x: dir * 40 }),
                center: { opacity: 1, x: 0 },
                exit: (dir: number) => ({ opacity: 0, x: dir * -40 }),
              }}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25 }}
              className="flex flex-col gap-5 rounded-2xl glass-card px-5 py-5"
            >
              <h2 className="text-xl font-bold text-gray-900">
                {(qi18n as Record<string, string>).question ?? ""}
              </h2>
              {(qi18n as Record<string, string>).description && (
                <p className="text-sm text-gray-500">
                  {(qi18n as Record<string, string>).description}
                </p>
              )}

              <div className="space-y-2">
                {question.choices.map((choice: SurveyChoice, ci: number) => {
                  const ci18n = getI18n(choice.i18n);
                  const selected = answers[question.id] === choice.id;
                  return (
                    <button
                      key={choice.id}
                      onClick={() => handleChoice(choice)}
                      disabled={answerMutation.isPending || completeMutation.isPending}
                      className={`flex w-full items-center gap-3 rounded-xl px-4 py-3.5 text-left text-sm transition-all duration-150 ${
                        selected
                          ? "bg-brand-50/90 ring-2 ring-brand-400/60 font-semibold text-brand-700"
                          : "bg-white/85 ring-1 ring-black/[0.07] text-gray-700 hover:bg-brand-50/70 hover:ring-brand-300/50"
                      }`}
                    >
                      <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs ${
                        selected ? "border-brand-500 bg-brand-500 text-white" : "border-gray-300"
                      }`}>
                        {String.fromCharCode(65 + ci)}
                      </span>
                      <span>{(ci18n as Record<string, string>).label ?? (ci18n as Record<string, string>).text ?? ""}</span>
                    </button>
                  );
                })}
              </div>

              {qIdx === total - 1 && !hasAnswered && (
                <p className="text-center text-xs text-gray-400">
                  {lang === "ko" ? "답변을 선택하면 자동으로 완료됩니다." : lang === "en" ? "Select an answer to complete." : lang === "zh" ? "选择答案自动完成。" : "回答を選択すると自動的に完了します。"}
                </p>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        <ConfirmModal
          open={showExitConfirm}
          title={s.nav.exitTitle}
          description={s.nav.exitDesc}
          confirmLabel={s.nav.exitConfirm}
          cancelLabel={s.nav.exitCancel}
          onConfirm={confirmExit}
          onCancel={() => setShowExitConfirm(false)}
        />
      </div>
    );
  }

  return null;
}

function FInput({ label, value, onChange, type = "text" }: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-gray-600">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="form-input"
      />
    </div>
  );
}

function FSelect({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-gray-600">{label}</label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="form-input pr-9"
        >
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <svg className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );
}

function FCheckbox({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5">
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-lg border-2 transition-colors ${
          checked ? "border-brand-500 bg-brand-500 text-white" : "border-gray-300 bg-white"
        }`}
      >
        {checked && (
          <svg viewBox="0 0 12 10" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={2}>
            <polyline points="1,5 4.5,8.5 11,1" />
          </svg>
        )}
      </button>
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  );
}

function SurveySkeleton() {
  return (
    <div className="flex min-h-screen flex-col pt-3">
      <div className="sticky top-3 mx-3 h-[46px] shrink-0 rounded-[20px] glass-nav" />
      <div className="mx-3 mt-2 h-[38px] shrink-0 rounded-[14px] glass-nav" />
      <div className="px-3 pt-3">
        <div className="space-y-4 rounded-2xl glass-card px-5 py-6">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-12 w-full rounded-2xl" />
          <Skeleton className="h-12 w-full rounded-2xl" />
          <Skeleton className="h-12 w-full rounded-2xl" />
          <Skeleton className="h-12 w-full rounded-2xl" />
        </div>
      </div>
    </div>
  );
}
